import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent } from 'react';
import {
  annotationRangesBySegment,
  filterAnnotations,
  normaliseSelection,
  selectionText,
  type AnchoredSelection,
  type AnnotationTextSegment,
  type SelectionPoint,
} from '../../../shared/annotations';
import { groupTranscriptParagraphs, type TranscriptParagraph } from '../../../shared/transcript/paragraphs';
import type {
  AnnotationAction,
  PublicAnnotation,
  PublicComment,
  PublicReviewer,
  TranscriptSegment,
} from '../../../shared/types';
import Eyebrow from '../Eyebrow';
import type { ToolbarAnchor, ToolbarSource } from './AnnotationToolbar';
import {
  actionLabels,
  actionRail,
  actionTone,
  DecisionIcon,
  formatReviewTime,
  reviewerAuthorLabel,
  reviewerInitials,
  reviewQuietButton,
} from './ReviewUi';

export interface BrowserSelection {
  selection: AnchoredSelection;
  quote: string;
  /** Selection centre and edges in document coordinates, for the toolbar caret. */
  anchor: ToolbarAnchor | null;
  source: ToolbarSource;
}

interface TranscriptPanelProps {
  interviewNumber?: number;
  interviewTitle?: string;
  speakerLabel?: string | null;
  duration?: number | null;
  segments: TranscriptSegment[];
  annotations: PublicAnnotation[];
  comments: PublicComment[];
  reviewers: PublicReviewer[];
  reviewer: PublicReviewer | null;
  activeSegmentId: string | null;
  onSeek: (seconds: number) => void;
  onSelect: (selection: BrowserSelection | null) => void;
  onEdit: (annotation: PublicAnnotation, action: AnnotationAction, note: string | null) => Promise<void>;
  onDelete: (annotation: PublicAnnotation) => Promise<void>;
  onComment: (annotation: PublicAnnotation) => void;
}

/** Controls that live inside the transcript and never carry a text selection. */
const INTERACTIVE_TRANSCRIPT_TARGETS = 'button, a, input, textarea, select, summary, [role="button"]';

/** Positions in the full paragraph list that survive the current filters. */
export function visibleTranscriptParagraphIndices(
  paragraphs: readonly TranscriptParagraph<TranscriptSegment>[],
  visibleSegmentIds: ReadonlySet<string>,
): number[] {
  const indices: number[] = [];
  paragraphs.forEach((paragraph, index) => {
    if (paragraph.cues.some((segment) => visibleSegmentIds.has(segment.id))) indices.push(index);
  });
  return indices;
}

export function nextTranscriptHitIndex(
  current: number,
  direction: -1 | 1,
  hitCount: number,
): number | null {
  if (hitCount === 0) return null;
  return (current + direction + hitCount) % hitCount;
}

function pointFromSelectionNode(root: HTMLElement, node: Node | null, offset: number): SelectionPoint | null {
  if (!node) return null;
  const element = node instanceof Element ? node : node.parentElement;
  const segment = element?.closest<HTMLElement>('[data-segment-id]');
  if (!segment || !root.contains(segment)) return null;
  const range = document.createRange();
  range.selectNodeContents(segment);
  try {
    range.setEnd(node, offset);
  } catch {
    return null;
  }
  return { segment_id: segment.dataset.segmentId ?? '', offset: range.toString().length };
}

/** Document coordinates, so the toolbar keeps its place as the page scrolls. */
function rectAnchor(rect: DOMRect): ToolbarAnchor {
  return {
    x: rect.left + rect.width / 2 + window.scrollX,
    y: rect.bottom + window.scrollY,
    top: rect.top + window.scrollY,
  };
}

function selectionAnchor(selection: Selection): ToolbarAnchor | null {
  if (selection.rangeCount === 0) return null;
  const rect = selection.getRangeAt(0).getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return rectAnchor(rect);
}

function elementAnchor(element: Element): ToolbarAnchor {
  return rectAnchor(element.getBoundingClientRect());
}

export type SelectionCapture = 'ignore' | 'clear' | 'capture';

/**
 * A drag that misses transcript text must clear the live selection rather than
 * leave the previous one showing. Pointer-ups on the controls sitting inside
 * the transcript are neither a selection nor a dismissal, so they are ignored.
 */
export function selectionCaptureOutcome(input: {
  interactiveTarget: boolean;
  collapsed: boolean;
  anchor: SelectionPoint | null;
  focus: SelectionPoint | null;
}): SelectionCapture {
  if (input.interactiveTarget) return 'ignore';
  if (input.collapsed || !input.anchor || !input.focus) return 'clear';
  return 'capture';
}

export interface AnnotationParagraphSpan {
  start: number;
  end: number;
}

/**
 * Which rendered paragraphs an annotation touches. Cross-paragraph highlights
 * are stored as one selection but render as per-segment fragments, so the rail
 * needs the whole run to stitch them back together.
 */
export function annotationParagraphSpan(
  paragraphs: readonly TranscriptParagraph<TranscriptSegment>[],
  annotation: Pick<PublicAnnotation, 'start_segment_id' | 'end_segment_id'>,
): AnnotationParagraphSpan | null {
  const start = paragraphs.findIndex((paragraph) => paragraph.cues.some((cue) => cue.id === annotation.start_segment_id));
  const end = paragraphs.findIndex((paragraph) => paragraph.cues.some((cue) => cue.id === annotation.end_segment_id));
  if (start < 0 || end < 0) return null;
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

export interface ParagraphRail {
  action: AnnotationAction;
  joinTop: boolean;
  joinBottom: boolean;
}

/**
 * One rail per rendered paragraph, joined at the edges it shares with the
 * paragraphs a highlight continues into. First annotation on a paragraph wins
 * its colour.
 *
 * Spans are always measured against the full paragraph list, then mapped onto
 * what is rendered, so a search filter cannot shrink a highlight's run or erase
 * its rail: a paragraph whose neighbour is filtered out simply keeps its own
 * rail and ends the run there. Rails are keyed by rendered position.
 */
export function paragraphRails(
  paragraphs: readonly TranscriptParagraph<TranscriptSegment>[],
  annotations: readonly PublicAnnotation[],
  renderedIndices?: readonly number[],
): { rails: Map<number, ParagraphRail>; spans: Map<string, AnnotationParagraphSpan> } {
  const rendered = renderedIndices ?? paragraphs.map((_, index) => index);
  const positionOf = new Map(rendered.map((index, position) => [index, position]));
  const rails = new Map<number, ParagraphRail>();
  const spans = new Map<string, AnnotationParagraphSpan>();
  for (const annotation of annotations) {
    const span = annotationParagraphSpan(paragraphs, annotation);
    if (!span) continue;
    spans.set(annotation.id, span);
    if (annotation.action === 'comment') continue;
    for (let index = span.start; index <= span.end; index += 1) {
      const position = positionOf.get(index);
      if (position === undefined) continue;
      const existing = rails.get(position);
      rails.set(position, {
        action: existing?.action ?? annotation.action,
        joinTop: (existing?.joinTop ?? false) || (index > span.start && positionOf.get(index - 1) === position - 1),
        joinBottom: (existing?.joinBottom ?? false) || (index < span.end && positionOf.get(index + 1) === position + 1),
      });
    }
  }
  return { rails, spans };
}

/** Dot order under the summary chip, matching the toolbar's button order. */
const SUMMARY_ACTIONS: readonly AnnotationAction[] = ['use', 'maybe', 'remove', 'comment'];

/** How each decision is counted out, singular and plural. */
const SUMMARY_NOUNS: Record<AnnotationAction, readonly [string, string]> = {
  use: ['use', 'uses'],
  maybe: ['maybe', 'maybes'],
  remove: ['remove', 'removes'],
  comment: ['comment', 'comments'],
};

function countOf(total: number, [one, many]: readonly [string, string]): string {
  return `${total} ${total === 1 ? one : many}`;
}

/** Two reviewers on one paragraph is where the chip row starts to pile up. */
export const PARAGRAPH_SUMMARY_THRESHOLD = 2;

export interface ParagraphAnnotationSummary {
  /** Distinct decisions present, in dot order. */
  actions: AnnotationAction[];
  /** Use, Maybe and Remove — the decisions someone marked. */
  selectCount: number;
  /** Comment annotations plus the discussion comments anchored on the paragraph,
   *  counting a comment annotation and its own comment as the one comment they are. */
  commentCount: number;
  /** Enough marks that the per-annotation chips fold behind the summary. */
  collapsed: boolean;
  /** What the chip shows: per-decision counts once the decisions differ. */
  label: string;
  /** Every decision counted out, for the chip's accessible name and tooltip. */
  description: string;
}

/**
 * One line standing in for every mark on a paragraph. A single annotation is
 * left alone — the chip is already the calmest thing we could show it as — but
 * past the threshold the row collapses to counts a reviewer can read at a
 * glance, with the aggregated comments pill folded into the comment count.
 *
 * Two decisions on one paragraph are counted out by name rather than totalled
 * as "selects": the dots beside them are the only other thing telling Use from
 * Remove, and colour alone cannot carry that. The full breakdown always goes to
 * the accessible name, whichever wording the chip is showing.
 */
export function paragraphAnnotationSummary(
  annotations: readonly Pick<PublicAnnotation, 'id' | 'action'>[],
  comments: readonly Pick<PublicComment, 'annotation_id'>[],
  threshold: number = PARAGRAPH_SUMMARY_THRESHOLD,
): ParagraphAnnotationSummary {
  const answered = new Set(comments.map((comment) => comment.annotation_id));
  const selectCount = annotations.filter((annotation) => annotation.action !== 'comment').length;
  // Saving a comment from the toolbar writes a comment annotation and anchors a
  // comment to it — one comment between them, however it is stored. Only a
  // comment annotation with nothing anchored to it adds to what the pill counts.
  const unanchored = annotations.filter((annotation) => annotation.action === 'comment' && !answered.has(annotation.id)).length;
  const commentCount = comments.length + unanchored;
  const totals = SUMMARY_ACTIONS
    .map((action) => ({
      action,
      total: action === 'comment' ? commentCount : annotations.filter((annotation) => annotation.action === action).length,
    }))
    .filter((entry) => entry.total > 0);
  const decisions = totals.filter((entry) => entry.action !== 'comment');
  const grouped: string[] = [];
  if (selectCount > 0) grouped.push(countOf(selectCount, ['select', 'selects']));
  if (commentCount > 0) grouped.push(countOf(commentCount, SUMMARY_NOUNS.comment));
  const breakdown = totals.map((entry) => countOf(entry.total, SUMMARY_NOUNS[entry.action]));
  return {
    actions: totals.map((entry) => entry.action),
    selectCount,
    commentCount,
    collapsed: annotations.length >= threshold,
    label: (decisions.length > 1 ? breakdown : grouped).join(' · '),
    description: breakdown.join(', '),
  };
}

function AnnotationDetail({ annotation, reviewer, own, onEdit, onDelete, onComment }: { annotation: PublicAnnotation; reviewer: PublicReviewer | undefined; own: boolean; onEdit: TranscriptPanelProps['onEdit']; onDelete: TranscriptPanelProps['onDelete']; onComment: TranscriptPanelProps['onComment'] }) {
  const [note, setNote] = useState(annotation.note ?? '');
  const [busyAction, setBusyAction] = useState<AnnotationAction | 'note' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setNote(annotation.note ?? ''), [annotation.note]);

  async function change(
    action: AnnotationAction,
    nextNote: string | null = note.trim() || null,
    feedback: AnnotationAction | 'note' = action,
  ) {
    if (action === 'comment' && !nextNote) {
      setError('Add the comment you want to leave.');
      return;
    }
    setBusyAction(feedback);
    setError(null);
    try {
      await onEdit(annotation, action, nextNote);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We could not save that change. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  async function remove() {
    setBusyAction('delete');
    setError(null);
    try {
      await onDelete(annotation);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We could not delete that annotation. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <details className="group relative" data-annotation-id={annotation.id}>
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-3 [&::-webkit-details-marker]:hidden" title={`${actionLabels[annotation.action]} by ${reviewerAuthorLabel(reviewer)}`}>
        <span className={`chip ${annotation.action === 'use' ? 'chip-success' : annotation.action === 'maybe' ? 'chip-warning' : annotation.action === 'remove' ? 'chip-error' : 'chip-teal'}`}>
          <DecisionIcon action={annotation.action} className="h-3.5 w-3.5" />
          <span>{actionLabels[annotation.action]} · {reviewerInitials(reviewer)}</span>
        </span>
        <span className="mark-link inline-flex items-center">Change</span>
      </summary>
      <div className="anno-detail-pop absolute right-0 z-20 mt-2 w-[min(20rem,calc(100vw-3rem))] rounded-card border border-line bg-card p-4 shadow-floating">
        <p className="text-sm font-semibold">{reviewerAuthorLabel(reviewer)}{reviewer?.is_owner ? <span className="owner-badge ml-2 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em]">Owner</span> : null}</p>
        {annotation.note ? <p className="mt-2 text-sm text-muted">{annotation.note}</p> : <p className="mt-2 text-xs text-faint">No note added.</p>}
        {own ? (
          <div className="mt-4 border-t border-line pt-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-faint">Change decision</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(['use', 'maybe', 'remove', 'comment'] as const).map((action) => <button key={action} type="button" className={`app-button inline-flex min-h-11 items-center justify-center gap-1 rounded-full border px-2 text-xs font-semibold ${actionTone[action]} ${busyAction === action ? 'ring-2 ring-current ring-offset-2' : ''}`} onClick={() => void change(action)} disabled={busyAction !== null} aria-busy={busyAction === action || undefined}>{busyAction === action ? <span className="inline-spinner" aria-hidden="true" /> : <DecisionIcon action={action} className="h-3.5 w-3.5" />}{busyAction === action ? 'Saving' : actionLabels[action]}</button>)}
            </div>
            <label className="mt-3 block font-mono text-[9px] uppercase tracking-[0.12em] text-faint">Note<textarea className="mt-1.5 min-h-16 w-full resize-y rounded-input border border-line bg-page p-2 font-sans text-sm font-normal normal-case tracking-normal text-fg" value={note} onChange={(event) => setNote(event.target.value)} /></label>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className={reviewQuietButton} onClick={() => void change(annotation.action, note.trim() || null, 'note')} disabled={busyAction !== null} aria-busy={busyAction === 'note' || undefined}>{busyAction === 'note' ? <><span className="inline-spinner" aria-hidden="true" />Saving note</> : 'Save note'}</button>
              <button type="button" className={reviewQuietButton} onClick={() => onComment(annotation)} disabled={busyAction !== null}>Add comment</button>
              <button type="button" className={`${reviewQuietButton} text-error`} onClick={() => void remove()} disabled={busyAction !== null} aria-busy={busyAction === 'delete' || undefined}>{busyAction === 'delete' ? <><span className="inline-spinner" aria-hidden="true" />Deleting</> : 'Delete'}</button>
            </div>
            {error ? <p className="mt-3 text-xs text-error" role="alert">{error}</p> : null}
          </div>
        ) : null}
      </div>
    </details>
  );
}

export default function TranscriptPanel({ interviewNumber = 1, interviewTitle = 'Transcript', speakerLabel, duration, segments, annotations, comments, reviewers, reviewer, activeSegmentId, onSeek, onSelect, onEdit, onDelete, onComment }: TranscriptPanelProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const cueRefs = useRef(new Map<string, HTMLLIElement>());
  const manualUntil = useRef(0);
  const [showReturn, setShowReturn] = useState(false);
  const [query, setQuery] = useState('');
  const [reviewerFilter, setReviewerFilter] = useState('all');
  const [hitIndex, setHitIndex] = useState(0);
  const textSegments: AnnotationTextSegment[] = useMemo(() => segments.map(({ id, order_index, text, start_s, end_s }) => ({ id, order_index, text, start_s, end_s })), [segments]);
  const reviewerById = useMemo(() => new Map(reviewers.map((item) => [item.id, item])), [reviewers]);

  const matchingAnnotations = useMemo(() => filterAnnotations(annotations, textSegments, {
    reviewer_id: reviewerFilter === 'all' ? null : reviewerFilter,
    query: query || undefined,
  }), [annotations, query, reviewerFilter, textSegments]);

  const matchingIds = useMemo(() => new Set(matchingAnnotations.map((annotation) => annotation.id)), [matchingAnnotations]);
  const rangesBySegment = useMemo(
    () => annotationRangesBySegment(textSegments, annotations),
    [annotations, textSegments],
  );

  const visibleSegments = useMemo(() => {
    const hasAnnotationFilter = reviewerFilter !== 'all';
    const lowerQuery = query.trim().toLocaleLowerCase('en-AU');
    return segments.filter((segment) => {
      const annotationMatch = rangesBySegment.get(segment.id)?.some((range) => matchingIds.has(range.id)) ?? false;
      const textMatch = !lowerQuery || segment.text.toLocaleLowerCase('en-AU').includes(lowerQuery);
      if (hasAnnotationFilter && lowerQuery) return annotationMatch;
      if (hasAnnotationFilter) return annotationMatch;
      if (lowerQuery) return textMatch || annotationMatch;
      return true;
    });
  }, [matchingIds, query, rangesBySegment, reviewerFilter, segments]);
  const paragraphs = useMemo(() => groupTranscriptParagraphs(segments), [segments]);
  const visibleSegmentIds = useMemo(() => new Set(visibleSegments.map((segment) => segment.id)), [visibleSegments]);
  const visibleIndices = useMemo(
    () => visibleTranscriptParagraphIndices(paragraphs, visibleSegmentIds),
    [paragraphs, visibleSegmentIds],
  );
  const visibleParagraphs = useMemo(
    () => visibleIndices.map((index) => paragraphs[index]!),
    [paragraphs, visibleIndices],
  );

  // Measured against every paragraph, drawn on the rendered ones: a search that
  // hides one end of a highlight must not take the rest of its rail with it.
  const { rails, spans } = useMemo(
    () => paragraphRails(paragraphs, annotations, visibleIndices),
    [annotations, paragraphs, visibleIndices],
  );

  useEffect(() => {
    setHitIndex((current) => Math.min(current, Math.max(0, visibleParagraphs.length - 1)));
  }, [visibleParagraphs.length]);

  useEffect(() => {
    function manualScroll() {
      manualUntil.current = Date.now() + 5_000;
    }
    window.addEventListener('wheel', manualScroll, { passive: true });
    window.addEventListener('touchmove', manualScroll, { passive: true });
    return () => {
      window.removeEventListener('wheel', manualScroll);
      window.removeEventListener('touchmove', manualScroll);
    };
  }, []);

  useEffect(() => {
    if (!activeSegmentId) return;
    if (Date.now() < manualUntil.current) {
      setShowReturn(true);
      return;
    }
    cueRefs.current.get(activeSegmentId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setShowReturn(false);
  }, [activeSegmentId]);

  function returnToCurrent() {
    manualUntil.current = 0;
    if (activeSegmentId) cueRefs.current.get(activeSegmentId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setShowReturn(false);
  }

  function captureSelection(event: ReactMouseEvent | ReactTouchEvent) {
    const target = event.target instanceof Element ? event.target : null;
    const interactiveTarget = Boolean(target?.closest(INTERACTIVE_TRANSCRIPT_TARGETS));
    window.setTimeout(() => {
      const browserSelection = window.getSelection();
      const root = rootRef.current;
      if (!root) return;
      const collapsed = !browserSelection || browserSelection.isCollapsed;
      const anchor = browserSelection && !collapsed
        ? pointFromSelectionNode(root, browserSelection.anchorNode, browserSelection.anchorOffset)
        : null;
      const focus = browserSelection && !collapsed
        ? pointFromSelectionNode(root, browserSelection.focusNode, browserSelection.focusOffset)
        : null;
      const outcome = selectionCaptureOutcome({ interactiveTarget, collapsed, anchor, focus });
      if (outcome === 'ignore') return;
      if (outcome === 'clear' || !anchor || !focus || !browserSelection) {
        onSelect(null);
        return;
      }
      try {
        const selection = normaliseSelection(textSegments, anchor, focus);
        if (!selection) {
          onSelect(null);
          return;
        }
        onSelect({
          selection,
          quote: selectionText(textSegments, selection),
          anchor: selectionAnchor(browserSelection),
          source: 'text',
        });
      } catch {
        onSelect(null);
      }
    }, 0);
  }

  function selectParagraph(paragraph: TranscriptParagraph<TranscriptSegment>) {
    const firstSegment = paragraph.cues[0];
    const lastSegment = paragraph.cues.at(-1);
    if (!firstSegment || !lastSegment) return;
    const selection: AnchoredSelection = {
      start_segment_id: firstSegment.id,
      start_offset: 0,
      end_segment_id: lastSegment.id,
      end_offset: lastSegment.text.length,
    };
    const cue = cueRefs.current.get(firstSegment.id);
    onSelect({
      selection,
      quote: selectionText(textSegments, selection),
      anchor: cue ? elementAnchor(cue) : null,
      source: 'paragraph',
    });
  }

  function navigateHit(direction: -1 | 1) {
    const next = nextTranscriptHitIndex(hitIndex, direction, visibleParagraphs.length);
    if (next === null) return;
    setHitIndex(next);
    const paragraph = visibleParagraphs[next]!;
    const firstSegment = paragraph.cues[0]!;
    cueRefs.current.get(firstSegment.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    onSeek(paragraph.start_s);
  }

  function openAnnotationDetail(annotation: Pick<PublicAnnotation, 'id'>) {
    const details = rootRef.current?.querySelector<HTMLDetailsElement>(`details[data-annotation-id="${CSS.escape(annotation.id)}"]`);
    if (!details) return;
    // Clicking your own highlight has to reach the detail even when it is
    // folded inside a paragraph's summary group.
    for (let group = details.parentElement?.closest('details') ?? null; group; group = group.parentElement?.closest('details') ?? null) {
      group.open = true;
    }
    details.open = true;
    details.querySelector<HTMLElement>('summary')?.focus();
  }

  /** One annotation's chips — the detail disclosure and its span note. */
  function renderAnnotationChips(annotation: PublicAnnotation) {
    const span = spans.get(annotation.id);
    const spanned = span ? span.end - span.start + 1 : 1;
    return (
      <>
        <AnnotationDetail annotation={annotation} reviewer={reviewerById.get(annotation.reviewer_id)} own={reviewer?.id === annotation.reviewer_id} onEdit={onEdit} onDelete={onDelete} onComment={onComment} />
        {spanned > 1 ? <span className="span-chip">Spans {spanned} paragraphs</span> : null}
      </>
    );
  }

  function renderText(segment: TranscriptSegment) {
    const ranges = rangesBySegment.get(segment.id) ?? [];
    const boundaries = [...new Set([0, segment.text.length, ...ranges.flatMap((range) => [range.start, range.end])])].sort((a, b) => a - b);
    return boundaries.slice(0, -1).map((start, index) => {
      const end = boundaries[index + 1]!;
      const active = ranges.filter((range) => range.start <= start && range.end >= end).sort((left, right) => left.layer - right.layer);
      const remove = active.some((range) => range.action === 'remove');
      // The edit affordance points at a chip, so it only offers itself while
      // the filters are leaving that chip on the row to open.
      const ownAnnotation = active.find((range) => range.reviewer_id === reviewer?.id && matchingIds.has(range.id));
      return (
        <span
          key={`${start}-${end}`}
          className={`${active.length ? 'rounded-[2px] bg-paper-200' : ''} ${ownAnnotation ? 'group/own-annotation cursor-pointer' : ''}` || undefined}
          style={{
            boxShadow: active.map((range) => `inset 0 -${2 + range.layer * 3}px 0 ${actionRail[range.action]}`).join(', '),
            textDecorationLine: remove ? 'line-through' : undefined,
            textDecorationThickness: remove ? '1.5px' : undefined,
          }}
          onClick={ownAnnotation ? (event) => {
            event.stopPropagation();
            openAnnotationDetail(ownAnnotation);
          } : undefined}
          title={ownAnnotation ? 'Edit this decision' : undefined}
        >
          {segment.text.slice(start, end)}
          {ownAnnotation ? (
            <span className="ml-1 hidden items-center gap-0.5 rounded-full bg-accent-tint px-1.5 py-0.5 align-middle font-sans text-[10px] font-semibold leading-none text-accent after:content-['Edit'] group-hover/own-annotation:inline-flex" aria-hidden="true">
              <svg className="h-2.5 w-2.5" viewBox="0 0 20 20"><path d="m4 13-.8 3.8L7 16l8.5-8.5-3-3zM11.5 5.5l3 3" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" /></svg>
            </span>
          ) : null}
        </span>
      );
    });
  }

  if (segments.length === 0) {
    return <section className="rounded-card border border-dashed border-line bg-paper-200 p-8"><h2 className="text-3xl">No transcript is ready yet</h2><p className="mt-3 text-muted">We could not find transcript cues for this interview. Questions? Reply to Henry&apos;s email or call.</p></section>;
  }

  return (
    <section aria-labelledby="transcript-title" className="doc-card rise-subtle" style={{ animationDelay: '420ms' }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow accent>Interview {interviewNumber}</Eyebrow>
          <h2 id="transcript-title" className="title-lg mt-3">{interviewTitle}</h2>
          <p className="eyebrow mt-2">{speakerLabel || 'Speaker not set'} · {segments.length} cues · {formatReviewTime(duration)}</p>
        </div>
        {showReturn ? <button type="button" className={reviewQuietButton} onClick={returnToCurrent}>Back to current paragraph</button> : null}
      </div>
      <div className="mt-7 grid gap-3 rounded-card bg-paper-200 p-4 sm:grid-cols-2 xl:grid-cols-[minmax(12rem,1fr)_auto]">
        <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Search transcript<input className="mt-1.5 min-h-11 w-full rounded-input border border-line bg-card px-3 font-sans text-sm font-normal normal-case tracking-normal text-fg" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a word or note" /></label>
        <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Reviewer<select className="mt-1.5 min-h-11 w-full rounded-input border border-line bg-card px-3 font-sans text-sm font-normal normal-case tracking-normal text-fg" value={reviewerFilter} onChange={(event) => setReviewerFilter(event.target.value)}><option value="all">Everyone</option>{reviewers.map((item) => <option key={item.id} value={item.id}>{item.display_name}</option>)}</select></label>
        {query ? <div className="flex items-end gap-2 sm:col-span-2"><p className="mr-auto text-xs text-faint" aria-live="polite">{visibleParagraphs.length} {visibleParagraphs.length === 1 ? 'hit' : 'hits'}</p><button type="button" className={reviewQuietButton} onClick={() => navigateHit(-1)} disabled={visibleParagraphs.length === 0}>Previous hit</button><button type="button" className={reviewQuietButton} onClick={() => navigateHit(1)} disabled={visibleParagraphs.length === 0}>Next hit</button></div> : null}
      </div>
      <div ref={rootRef} className="mt-6" onMouseUp={captureSelection} onTouchEnd={captureSelection}>
        {visibleSegments.length === 0 ? <div className="rounded-card border border-dashed border-line p-8 text-center"><h3 className="text-2xl">Nothing matches those filters</h3><p className="mt-2 text-sm text-muted">Clear a filter or try another search.</p></div> : (
          <ol>
            {visibleParagraphs.map((paragraph, paragraphIndex) => {
              const paragraphSegmentIds = new Set(paragraph.cues.map((segment) => segment.id));
              const paragraphAnnotations = annotations.filter((annotation) => paragraphSegmentIds.has(annotation.start_segment_id));
              // Chips and their summary both count the marks the filters left
              // standing, so a search cannot leave the count claiming more than
              // the row shows. Rails and highlights still run off every mark.
              const shownAnnotations = paragraphAnnotations.filter((annotation) => matchingIds.has(annotation.id));
              const paragraphAnnotationIds = new Set(paragraphAnnotations.map((annotation) => annotation.id));
              const paragraphComments = comments.filter((comment) =>
                (comment.annotation_id !== null && paragraphAnnotationIds.has(comment.annotation_id))
                || (comment.time_s !== null && paragraph.cues.some((segment) => comment.time_s! >= segment.start_s && comment.time_s! < segment.end_s)));
              const active = paragraph.cues.some((segment) => segment.id === activeSegmentId);
              const firstSegment = paragraph.cues[0]!;
              const rail = rails.get(paragraphIndex);
              // The rail runs the whole highlight, but only the paragraph a
              // decision starts on takes its styling: a remove that continues
              // into the next paragraph must not strike through text nobody
              // marked, and it must not dim a Use that starts there either.
              const marked = paragraphAnnotations.find((annotation) => annotation.action !== 'comment')?.action ?? null;
              const summary = paragraphAnnotationSummary(shownAnnotations, paragraphComments);
              const commentsPill = paragraphComments.length > 0
                ? <button type="button" className="inline-flex min-h-11 items-center rounded-full border border-accent/25 bg-accent-tint px-3 text-xs font-semibold text-accent" onClick={() => onSeek(paragraphComments[0]?.time_s ?? paragraph.start_s)}><DecisionIcon action="comment" className="mr-1 h-3.5 w-3.5" />{paragraphComments.length} {paragraphComments.length === 1 ? 'comment' : 'comments'}</button>
                : null;
              const markButton = (
                <button type="button" className="app-button para-mark" onClick={() => selectParagraph(paragraph)}>
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" aria-hidden="true"><path d="m4 13-.8 3.8L7 16l8.5-8.5-3-3zM11.5 5.5l3 3" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" /></svg>
                  Mark this paragraph
                </button>
              );
              return (
                <li
                  key={firstSegment.id}
                  ref={(node) => {
                    for (const segment of paragraph.cues) {
                      if (node) cueRefs.current.set(segment.id, node);
                      else cueRefs.current.delete(segment.id);
                    }
                  }}
                  className={`cue scroll-mt-72 transition lg:scroll-mt-24 ${rail ? `rail-${rail.action}` : ''} ${marked ? `marked-${marked}` : ''} ${rail?.joinTop ? 'rail-join-top' : ''} ${rail?.joinBottom ? 'rail-join-bottom' : ''} ${active ? 'bg-accent-tint/60' : ''}`}
                  aria-current={active ? 'true' : undefined}
                >
                    <button type="button" className="min-h-11 self-start rounded-input px-2 text-left font-mono text-[11px] text-faint hover:bg-paper-200 hover:text-accent" onClick={() => onSeek(paragraph.start_s)} aria-label={`Seek video to ${formatReviewTime(paragraph.start_s)}`}>{formatReviewTime(paragraph.start_s)}</button>
                    <div>
                      {paragraph.speaker_label ? <p className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">{paragraph.speaker_label}</p> : null}
                      <p className="cue-text min-h-11 text-base leading-[1.65] text-fg">
                        {paragraph.cues.map((segment, index) => (
                          <span key={segment.id} data-segment-id={segment.id} className={index < paragraph.cues.length - 1 ? 'mr-1' : undefined}>
                            {renderText(segment)}
                          </span>
                        ))}
                      </p>
                      {/* One shape either side of the threshold: the group chrome
                          switches on above it, but every detail keeps the same
                          parent, so deleting a mark and dropping back under the
                          threshold cannot remount the survivor and throw away
                          the note its author was part way through writing. */}
                      <div className={`mt-3 flex flex-wrap gap-2 ${summary.collapsed ? 'items-start' : 'items-center'}`}>
                        {shownAnnotations.length > 0 || commentsPill ? (
                        <details className={`anno-group ${summary.collapsed ? 'min-w-0' : 'anno-group-flat'}`} open={summary.collapsed ? undefined : true}>
                          <summary
                            className={summary.collapsed ? 'anno-group-chip' : 'anno-group-summary-flat'}
                            title={`${summary.description} on this paragraph`}
                            aria-label={`${summary.description} on this paragraph`}
                            aria-hidden={summary.collapsed ? undefined : true}
                            tabIndex={summary.collapsed ? undefined : -1}
                          >
                            <span className="anno-group-dots" aria-hidden="true">
                              {summary.actions.map((action) => <span key={action} className={`anno-group-dot anno-group-dot-${action}`} />)}
                            </span>
                            {summary.label}
                            <svg className="anno-group-caret h-3 w-3" viewBox="0 0 20 20" aria-hidden="true"><path d="m5 8 5 5 5-5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>
                          </summary>
                          <div className="anno-group-body">
                            {shownAnnotations.map((annotation) => <div key={annotation.id} className="anno-group-row">{renderAnnotationChips(annotation)}</div>)}
                            {commentsPill}
                          </div>
                        </details>
                        ) : null}
                        {markButton}
                      </div>
                    </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
